import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, DollarSign, Users, Award, ChevronDown, ChevronUp,
  Wallet, Gift, ArrowUpRight, ArrowDownRight, History, Target,
  Star, Zap, Shield, RefreshCw
} from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import CommissionWithdrawal from './CommissionWithdrawal';

const CommissionDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [earnings, setEarnings] = useState(null);
  const [levelStatus, setLevelStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [rates, setRates] = useState(null);
  const [teamEarnings, setTeamEarnings] = useState(null);
  const [expandedLevel, setExpandedLevel] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [earningsRes, levelRes, historyRes, ratesRes, teamRes] = await Promise.all([
        api.get('/commission/my-earnings').catch(() => ({ data: null })),
        api.get('/commission/my-level-status').catch(() => ({ data: null })),
        api.get('/commission/history?limit=20').catch(() => ({ data: [] })),
        api.get('/commission/rates').catch(() => ({ data: null })),
        api.get('/commission/team-earnings').catch(() => ({ data: null }))
      ]);
      
      setEarnings(earningsRes.data);
      setLevelStatus(levelRes.data);
      setHistory(historyRes.data || []);
      setRates(ratesRes.data);
      setTeamEarnings(teamRes.data);
    } catch (error) {
      console.error('Failed to fetch commission data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case 'sankat_mochan': return <Zap className="w-4 h-4 text-purple-400" />;
      case 'agp': return <Shield className="w-4 h-4 text-cyan-400" />;
      case 'shop': return <Gift className="w-4 h-4 text-green-400" />;
      case 'p2p': return <Users className="w-4 h-4 text-blue-400" />;
      default: return <DollarSign className="w-4 h-4 text-yellow-400" />;
    }
  };

  const getSourceLabel = (source) => {
    switch (source) {
      case 'sankat_mochan': return 'Pass Purchase';
      case 'agp': return 'AGP Transaction';
      case 'shop': return 'Shop Sale';
      case 'p2p': return 'P2P Transfer';
      default: return source;
    }
  };

  if (loading) {
    return (
      <div className="glass-card p-8 animate-pulse" data-testid="commission-loading">
        <div className="h-8 bg-white/10 rounded w-1/3 mb-6"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-32 bg-white/10 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="commission-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-yellow-500/20 to-amber-500/20">
            <TrendingUp className="w-8 h-8 text-yellow-500" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">Commission Dashboard</h2>
            <p className="text-muted-foreground text-sm">Track your Affiliate Network earnings in real-time</p>
          </div>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-all"
          data-testid="refresh-commission"
        >
          <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Earned */}
        <div className="glass-card p-5 border border-green-500/20" data-testid="stat-total-earned">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-muted-foreground">Total Earned</span>
            <DollarSign className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-2xl font-bold text-green-500">{formatCurrency(earnings?.total_earned)}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {earnings?.total_transactions || 0} transactions
          </p>
        </div>

        {/* Commission Wallet */}
        <div className="glass-card p-5 border border-yellow-500/20" data-testid="stat-commission-wallet">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-muted-foreground">Commission Wallet</span>
            <Wallet className="w-5 h-5 text-yellow-500" />
          </div>
          <p className="text-2xl font-bold text-yellow-500">{formatCurrency(earnings?.wallet_balance)}</p>
          <p className="text-xs text-muted-foreground mt-1">Available for withdrawal</p>
        </div>

        {/* Current Level */}
        <div className="glass-card p-5 border border-purple-500/20" data-testid="stat-current-level">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-muted-foreground">Current Level</span>
            <Award className="w-5 h-5 text-purple-500" />
          </div>
          <p className="text-2xl font-bold text-purple-500">Level {levelStatus?.unlocked_level || 1}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {levelStatus?.direct_referrals || 0} direct referrals
          </p>
        </div>

        {/* Team Size */}
        <div className="glass-card p-5 border border-blue-500/20" data-testid="stat-team-size">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-muted-foreground">Team Size</span>
            <Users className="w-5 h-5 text-blue-500" />
          </div>
          <p className="text-2xl font-bold text-blue-500">{teamEarnings?.total_team_members || 0}</p>
          <p className="text-xs text-muted-foreground mt-1">Total network members</p>
        </div>
      </div>

      {/* Level Breakdown & Progress */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Commission by Level */}
        <div className="glass-card p-6" data-testid="level-breakdown">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-primary" />
            Earnings by Level
          </h3>
          
          <div className="space-y-3">
            {rates?.rates?.map((rate, idx) => {
              const levelEarning = earnings?.level_breakdown?.[`level_${rate.level}`] || 0;
              const isUnlocked = (levelStatus?.unlocked_level || 1) >= rate.level;
              
              return (
                <div 
                  key={rate.level}
                  className={`p-4 rounded-xl border transition-all cursor-pointer ${
                    isUnlocked 
                      ? 'bg-gradient-to-r from-white/5 to-white/10 border-white/20 hover:border-primary/50' 
                      : 'bg-white/5 border-white/10 opacity-60'
                  }`}
                  onClick={() => setExpandedLevel(expandedLevel === rate.level ? null : rate.level)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        isUnlocked ? 'bg-primary/20 text-primary' : 'bg-white/10 text-muted-foreground'
                      }`}>
                        {rate.level}
                      </div>
                      <div>
                        <p className="font-semibold">Level {rate.level}</p>
                        <p className="text-xs text-muted-foreground">
                          {rate.rate}% commission • {rate.directs_required} directs required
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold ${levelEarning > 0 ? 'text-green-500' : ''}`}>
                        {formatCurrency(levelEarning)}
                      </p>
                      {isUnlocked ? (
                        <span className="text-xs text-green-500 flex items-center gap-1">
                          <Star className="w-3 h-3" /> Unlocked
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">Locked</span>
                      )}
                    </div>
                  </div>
                  
                  {expandedLevel === rate.level && (
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Commission Rate</p>
                          <p className="font-bold text-lg">{rate.rate}%</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Directs Needed</p>
                          <p className="font-bold text-lg">{rate.directs_required}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="glass-card p-6" data-testid="recent-transactions">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <History className="w-5 h-5 text-primary" />
            Recent Commission History
          </h3>
          
          {history.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Gift className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No commission history yet</p>
              <p className="text-sm">Start referring to earn commissions!</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {history.map((tx, idx) => (
                <div 
                  key={tx.id || idx}
                  className="flex items-center justify-between p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-all"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-white/10">
                      {getSourceIcon(tx.source)}
                    </div>
                    <div>
                      <p className="font-medium">{getSourceLabel(tx.source)}</p>
                      <p className="text-xs text-muted-foreground">
                        Level {tx.level} • From @{tx.buyer_username || 'user'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-green-500 flex items-center gap-1">
                      <ArrowUpRight className="w-4 h-4" />
                      {formatCurrency(tx.amount)}
                    </p>
                    <p className="text-xs text-muted-foreground">{formatDate(tx.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Commission Rates Info */}
      {rates?.info && (
        <div className="glass-card p-6 border border-primary/20" data-testid="commission-info">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Star className="w-5 h-5 text-primary" />
            How Commission Works
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {rates.info.map((info, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-sm font-bold shrink-0">
                  {idx + 1}
                </div>
                <p className="text-sm text-muted-foreground">{info}</p>
              </div>
            ))}
          </div>
          
          <div className="mt-4 pt-4 border-t border-white/10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-primary">{rates.total_commission}%</p>
                <p className="text-xs text-muted-foreground">Total Commission</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-green-500">{rates.promotion_rate}%</p>
                <p className="text-xs text-muted-foreground">Promotion Rate</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-500">7</p>
                <p className="text-xs text-muted-foreground">Levels Deep</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-500">{rates.total_distribution}%</p>
                <p className="text-xs text-muted-foreground">Total Distribution</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Withdrawal Section */}
      <CommissionWithdrawal />
    </div>
  );
};

export default CommissionDashboard;
